import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {AuthenticationService} from '../../../../shared/Services/authentication.service';
import {FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import {environment} from '../../../../../environments/environment';
import {SystemUser} from '../../../../shared/Models/SystemUser';
import {DealerUserService} from '../../../Services/dealer-user.service';
import {DealerRole} from '../../../Models/DealerRole';
import {BroadcastService} from '../../../../shared/Services/broadcast.service';
import {UntypedFormGroup} from '@angular/forms';
import {UsersService} from '../../../../back-office/Services/users.service';
import {LanguageService} from '../../../../translation/Services/language.service';


@Component({
  selector: 'app-manage-user',
  templateUrl: './manage-user.component.html',
  styleUrls: ['./manage-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ManageUserComponent implements OnInit {
  public modalTitle;
  public isEdit = false;
  public lastValue: any;
  public enableSave = true;
  public roleId = 2;
  public languageId = null;
  public roles = [];
  public cookie;
  public languages = [];
  public manageUserForm;
  public options: FormlyFormOptions = {};
  public classicForm = new UntypedFormGroup({});
  @ViewChild('manageUser', {static: false}) manageUser;
  @ViewChild('modal', {static: true}) modal;
  @Output() notifyParent: EventEmitter<any> = new EventEmitter();
  @Input() userInput;
  @Input() manageProfile = false;
  @Input() userEditCondition = false;
  @Input() showModal = false;
  @Input() refresh: number;
  // current delivery user to add user to it
  @Input() currentDelivery = 0;
  @Input() canAddAdmin = false;
  @Input() isFirstUser!: boolean;

  constructor(
    private translator: TranslateService,
    private userService: UsersService,
    public languageService: LanguageService,
    private dealerUserService: DealerUserService,
    private auth: AuthenticationService,
    private broadcastService: BroadcastService,
  ) {
  }

  ngOnInit() {
    this.languageService.list().subscribe(response => {
      response.languages.map((value) => {
        this.languages.push({
          label: value.name,
          value: value.id
        });
      });
      this.initialForm();
    });
  }

  ngOnChanges(...args: any[]) {
    this.broadcastService.on('refreshModal').subscribe(res => {
      this.userInput = res;
    });
    this.initialForm();
  }

  initialForm() {
    this.modal.show();
    this.cookie = this.auth.getRole();
    if (this.cookie === 'ROLE_BACK_OFFICE') {
      this.userService.getUserRoles().subscribe((roles) => {
        const roleItem = roles.data;
        if (this.roles.length === 0) {
          roleItem.map((value) => {
            this.roles.push({
              label: this.translator.instant(value.name),
              value: value.id,
              disabled: false
            });
          });
        }
        if (this.userInput && this.userInput.id) {
          this.isEdit = true;
          if (this.userInput.role && typeof this.userInput.role !== 'number') {
            this.roleId = this.userInput.role.id;
          }
          if (this.userInput.language && typeof this.userInput.language !== 'number') {
            this.languageId = this.userInput.language.id;
          }
          this.modalTitle = this.manageProfile ? this.translator.instant('admin.dealer.account.update.information') : this.translator.instant('admin.dealer.account.edit_user');
        } else {
          this.userInput = new SystemUser();
          this.manageProfile = false;
          this.isEdit = false;
          this.modalTitle = this.translator.instant('admin.dealer.account.add_user');
        }
        this.userInput.role = this.userInput.role ? this.roles.find(role => role.value === this.roleId).value
          : (this.isFirstUser ?
            this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_ADMIN)).value :
            this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_MODERATOR)).value);
        this.userInput.language = this.userInput.language ? this.languages.find(language => language.value === this.languageId).value
          : this.languages.find(language => language.label === 'Nederlands').value;
        this.lastValue = {user: this.userInput, auth: this.userInput};
        this.manageUserForm = <FormlyFieldConfig>[
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                className: 'col-6 ',
                type: 'input',
                key: 'firstName',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.firstName'),
                  required: true,
                  pattern: '[a-zA-Z _-]+',
                  maxLength: environment.maxStringLength,
                },
                validation: {
                  messages: {
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
              {
                className: 'col-6',
                type: 'input',
                key: 'lastName',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.lastName'),
                  required: true,
                  pattern: '[a-zA-Z _-]+',
                  maxLength: environment.maxStringLength,
                },
                validation: {
                  messages: {
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
            ],
          },
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                className: 'col-6 ',
                type: 'input',
                key: 'username',
                pattern: '[a-zA-Z _-]+',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.username'),
                  required: true,
                  maxLength: environment.maxStringLength,
                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.manageProfile;
                    }
                  },
                },
                validation: {
                  messages: {
                    required: this.translator.instant('errors.required'),
                    pattern: this.translator.instant('errors.invalid'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
              {
                className: 'col-6 ',
                type: 'input',
                key: 'email',

                templateOptions: {
                  label: this.translator.instant('doctrine.user.email'),
                  required: true, // this.auth.isDealerAdmin() && this.manageProfile,
                  maxLength: environment.maxStringLength,
                  pattern: environment.emailPattern,
                  description: this.translator.instant('doctrine.user.email_alert')

                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.userEditCondition;
                    }
                  },
                },
                validation: {
                  messages: {
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
            ],
          },
          {
            fieldGroupClassName: 'row',
            key: 'auth',
            validators: {
              fieldMatch: {
                expression: (control) => {
                  const value = control.value;
                  if (!this.manageProfile || !this.isEdit) {
                    return value.plainPasswordConfirm === value.plainPassword || (!value.plainPasswordConfirm && !value.plainPassword);
                  }
                  return true;
                },
                message: this.translator.instant('errors.passwordMatch'),
                errorPath: 'plainPasswordConfirm',
              },
            },
            fieldGroup: [
              {
                className: 'col-6',
                type: 'input',
                key: 'plainPassword',
                /*     hideExpression: (model) => {
                       return this.manageProfile;
                     },*/
                templateOptions: {
                  label: this.translator.instant('doctrine.user.plainPassword'),
                  required: !this.isEdit,
                  type: 'password',
                  minLength: environment.minPassword,
                  maxLength: environment.maxStringLength,
                },
                validation: {
                  messages: {
                    minLength: this.translator.instant('validators.password.length.min',
                      {limit: environment.minPassword}),
                    maxLength: this.translator.instant('validators.password.length.max',
                      {limit: environment.maxStringLength}),
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    lowerCase: this.translator.instant('validators.password.contain.lowercase'),
                    uppercase: this.translator.instant('validators.password.contain.uppercase'),
                    number: this.translator.instant('validators.password.contain.number'),
                    special: this.translator.instant('validators.password.contain.special'),
                  },
                },
                validators:  {
                  validation: ['lowerCase', 'uppercase', 'number', 'special'],
                },
              },
              {
                className: 'col-6',
                type: 'input',
                key: 'plainPasswordConfirm',
                /*        hideExpression: (model) => {
                          return this.manageProfile;
                        },*/
                templateOptions: {
                  type: 'password',
                  label: this.translator.instant('doctrine.user.plainPasswordConfirm'),
                  required: !this.isEdit
                }
              }]
          },
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                key: 'role',
                type: 'ng-select',
                className: 'col-6 form-group',
                defaultValue: this.isFirstUser ?
                  this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_ADMIN)).value :
                  this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_MODERATOR)).value,
                templateOptions: {
                  label: this.translator.instant('doctrine.user.role'),
                  required: true,
                  options: this.roles,
                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.manageProfile || this.userEditCondition;
                    }
                  },
                },
                validation: {
                  messages: {
                    required: this.translator.instant('errors.required'),
                  },
                },
              },
              {
                key: 'language',
                type: 'ng-select',
                className: 'col-6 form-group',
                templateOptions: {
                  label: this.translator.instant('doctrine.attributes.language'),
                  options: this.languages,
                  required: true,
                },
              }
            ]
          },
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                key: 'status',
                type: 'ng-select',
                className: 'col-6 ',
                defaultValue: SystemUser.SYSTEMUSER_ACTIVE,
                templateOptions: {
                  label: this.translator.instant('doctrine.user.status'),
                  required: true,
                  options: [
                    {label: this.translator.instant('global.active'), value: SystemUser.SYSTEMUSER_ACTIVE},
                    {label: this.translator.instant('global.inactive'), value: SystemUser.SYSTEMUSER_INACTIVE},
                  ],
                },
                validation: {
                  messages: {
                    required: this.translator.instant('errors.required'),
                  },
                },
              },
            ],


          },
        ];
      });

    } else {
      this.dealerUserService.getUserRoles().subscribe((roles) => {
        const roleItem = roles.data;
        if (this.roles.length === 0) {
          roleItem.map((value) => {
            this.roles.push({
              label: this.translator.instant(value.name),
              value: value.id,
              disabled: value.name === DealerRole.ROLE_ADMIN
            });
          });
        }
        if (this.userInput && this.userInput.id) {
          this.isEdit = true;
          if (this.userInput.role && typeof this.userInput.role !== 'number') {
            this.roleId = this.userInput.role.id;
          }
          if (this.userInput.language && typeof this.userInput.language !== 'number') {
            this.languageId = this.userInput.language.id;
          }
          this.modalTitle = this.manageProfile ?
            this.translator.instant('admin.dealer.account.update.information') : this.translator.instant('admin.dealer.account.edit_user');
        } else {
          this.userInput = new SystemUser();
          this.manageProfile = false;
          this.isEdit = false;
          this.modalTitle = this.translator.instant('admin.dealer.account.add_user');
        }
        this.userInput.role = this.userInput.role ? this.roles.find(role => role.value === this.roleId).value
          : (this.isFirstUser ?
            this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_ADMIN)).value :
            this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_MODERATOR)).value);

        this.userInput.language = this.userInput.language ? this.languages.find(language => language.value === this.languageId).value
          : this.languages.find(language => language.label === 'Nederlands').value;

        this.lastValue = {user: this.userInput, auth: this.userInput};
        this.manageUserForm = <FormlyFieldConfig>[
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                className: 'col-6 ',
                type: 'input',
                key: 'firstName',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.firstName'),
                  required: true,
                  pattern: '[a-zA-Z _-]+',
                  maxLength: environment.maxStringLength,
                },
                validation: {
                  messages: {
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
              {
                className: 'col-6',
                type: 'input',
                key: 'lastName',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.lastName'),
                  required: true,
                  pattern: '[a-zA-Z _-]+',
                  maxLength: environment.maxStringLength,
                },
                validation: {
                  messages: {
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
            ],
          },
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                className: 'col-6 ',
                type: 'input',
                key: 'username',
                pattern: '[a-zA-Z _-]+',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.username'),
                  required: true,
                  maxLength: environment.maxStringLength,
                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.manageProfile;
                    }
                  },
                },
                validation: {
                  messages: {
                    required: this.translator.instant('errors.required'),
                    pattern: this.translator.instant('errors.invalid'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
              {
                className: 'col-6 ',
                type: 'input',
                key: 'email',
                templateOptions: {
                  label: this.translator.instant('doctrine.user.email'),
                  required: true, // this.auth.isDealerAdmin() && this.manageProfile,
                  maxLength: environment.maxStringLength,
                  pattern: environment.emailPattern,
                  description: this.translator.instant('doctrine.user.email_alert'),
                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.manageProfile;
                    }
                  },
                },
                validation: {
                  messages: {
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    maxlength: (error, field: FormlyFieldConfig) => this.translator.instant('validators.max_message',
                      {limit: field.templateOptions.maxLength})
                  },
                },
              },
            ],
          },
          {
            fieldGroupClassName: 'row',
            key: 'auth',
            validators: {
              fieldMatch: {
                expression: (control) => {
                  const value = control.value;
                  if (!this.manageProfile || !this.isEdit) {
                    return value.plainPasswordConfirm === value.plainPassword || (!value.plainPasswordConfirm && !value.plainPassword);
                  }
                  return true;
                },
                message: this.translator.instant('errors.passwordMatch'),
                errorPath: 'plainPasswordConfirm',
              },
            },
            fieldGroup: [
              {
                className: 'col-6',
                type: 'input',
                key: 'plainPassword',
                hideExpression: (model) => this.manageProfile,
                templateOptions: {
                  label: this.translator.instant('doctrine.user.plainPassword'),
                  required: !this.isEdit,
                  type: 'password',
                  minLength: environment.minPassword,
                  maxLength: environment.maxStringLength,
                },
                validation: {
                  messages: {
                    minLength: this.translator.instant('validators.password.length.min',
                      {limit: environment.minPassword}),
                    maxLength: this.translator.instant('validators.password.length.max',
                      {limit: environment.maxStringLength}),
                    pattern: this.translator.instant('errors.invalid'),
                    required: this.translator.instant('errors.required'),
                    lowerCase: this.translator.instant('validators.password.contain.lowercase'),
                    uppercase: this.translator.instant('validators.password.contain.uppercase'),
                    number: this.translator.instant('validators.password.contain.number'),
                    special: this.translator.instant('validators.password.contain.special'),
                  },
                },
                validators:  {
                  validation: ['lowerCase', 'uppercase', 'number', 'special'],
                },
              },
              {
                className: 'col-6',
                type: 'input',
                key: 'plainPasswordConfirm',
                hideExpression: (model) => this.manageProfile,
                templateOptions: {
                  type: 'password',
                  label: this.translator.instant('doctrine.user.plainPasswordConfirm'),
                  required: !this.isEdit
                }
              }]
          },
          {
            fieldGroupClassName: 'row',
            key: 'user',
            fieldGroup: [
              {
                key: 'role',
                type: 'ng-select',
                className: 'col-6 ',
                defaultValue: this.isFirstUser ?
                  this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_ADMIN)).value :
                  this.roles.find(role => role.label === this.translator.instant(DealerRole.ROLE_MODERATOR)).value,
                templateOptions: {
                  label: this.translator.instant('doctrine.user.role'),
                  required: true,
                  options: this.roles,
                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.manageProfile;
                    }
                  },
                },
                validation: {
                  messages: {
                    required: this.translator.instant('errors.required'),
                  },
                },
                hideExpression: (model) => !this.auth.isDealerAdmin()
              },
              {
                key: 'language',
                type: 'ng-select',
                className: 'col-6 form-group',
                templateOptions: {
                  label: this.translator.instant('doctrine.attributes.language'),
                  options: this.languages,
                  required: true,
                },
              },
              {
                key: 'status',
                type: 'ng-select',
                className: 'col-6 ',
                defaultValue: SystemUser.SYSTEMUSER_ACTIVE,
                templateOptions: {
                  label: this.translator.instant('doctrine.user.status'),
                  required: true,
                  options: [
                    {label: this.translator.instant('global.active'), value: SystemUser.SYSTEMUSER_ACTIVE},
                    {label: this.translator.instant('global.inactive'), value: SystemUser.SYSTEMUSER_INACTIVE},
                  ],
                },
                expressionProperties: {
                  'templateOptions.disabled': (model: any) => {
                    if (model !== undefined && model != null) {
                      return this.manageProfile;
                    }
                  },
                },
                validation: {
                  messages: {
                    required: this.translator.instant('errors.required'),
                  },
                },
                hideExpression: (model) => !this.auth.isDealerAdmin()
              },
            ],


          },
        ];
      });
    }


  }

  hideModal() {
    this.modal.hide();
    this.showModal = false;
    this.options.resetModel();
  }

  saveUser() {
    const id = this.userInput ? this.userInput.id : null;
    let user = new SystemUser();
    user = this.userInput ? this.userInput : this.lastValue.user;

    if (user.language && typeof(user.language) !== 'number'	&& user.language.id) {
      user.language = user.language.id;
    }

    if (user.parent && user.parent.id) {
      user.parent = user.parent.id;
    }

    if (this.cookie === 'ROLE_BACK_OFFICE') {
      if (this.currentDelivery !== 0) {
        user['parent'] = this.currentDelivery;
        this.userService.manageDeliveryDealerUser(user, this.isEdit, id).subscribe(res => {
            this.userInput = res;
            this.sendNotification();
          },
        );
      }
    } else {
      this.dealerUserService.manageUser(user, this.isEdit, id).subscribe(res => {
          this.userInput = res;
          this.sendNotification();
        },
      );
    }
    this.userInput = null;
    this.lastValue.user = null;
    this.hideModal();
  }

  enableButton(event) {
    this.enableSave = event;
  }

  sendNotification() {
    this.notifyParent.emit(this.userInput);
  }

}
